import QueueMusicIcon from '@mui/icons-material/QueueMusic'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { useEffect } from 'react'

import type { Job } from '../api/client'
import { useAuth } from '../auth/useAuth'
import JobRow from '../components/JobRow'

import { isActive } from '../queue/useJobs'
import { useJobsContext } from '../queue/useJobsContext'

/** How much finished work to keep on screen before it is just noise. */
const RECENT_LIMIT = 20

export default function QueuePage() {
  const { jobs, error, refreshNow } = useJobsContext()

  // Opening this screen refreshes it. The poll is shared with the sidebar now,
  // so arriving here no longer mounts a fresh one -- without this, the list
  // could be up to a poll interval stale at the moment somebody came to look.
  useEffect(() => {
    void refreshNow()
  }, [refreshNow])
  const { user } = useAuth()

  if (jobs === null) {
    return (
      <Stack alignItems="center" sx={{ pt: 6 }}>
        <CircularProgress />
      </Stack>
    )
  }

  const active = jobs.filter(isActive)
  const recent = jobs.filter((job) => !isActive(job)).slice(0, RECENT_LIMIT)

  // Everyone sees every job -- the GPU runs one at a time, so what is ahead of
  // you is what explains the wait. Only the owner or a sysadmin may act on one.
  const mayModify = (job: Job) => job.username === user?.username || user?.role === 'sysadmin'

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
          Queue
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => void refreshNow()}>
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="warning">{error}</Alert>}

      <Box>
        <Typography variant="overline" color="text.secondary">
          In progress
        </Typography>
        {active.length === 0 ? (
          <Paper sx={{ p: 3, mt: 1 }}>
            <Typography color="text.secondary">
              Nothing is queued or running. The GPU powers itself off when idle.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {active.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                canModify={mayModify(job)}
                onChanged={() => void refreshNow()}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Box>
        <Typography variant="overline" color="text.secondary">
          Recent
        </Typography>
        {recent.length === 0 ? (
          <Paper sx={{ p: 3, mt: 1 }}>
            <Stack spacing={1} alignItems="center" textAlign="center">
              <QueueMusicIcon color="disabled" sx={{ fontSize: 36 }} />
              <Typography color="text.secondary">
                Nothing finished yet. Order some covers and they will appear here.
              </Typography>
            </Stack>
          </Paper>
        ) : (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {recent.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                canModify={mayModify(job)}
                onChanged={() => void refreshNow()}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  )
}
